import { api } from './apiClient';

// Types
interface PurchaseRequest {
  id: string;
  requestNumber: string;
  requestedBy: string;
  department: string;
  description: string;
  items: PurchaseRequestItem[];
  totalAmount: number;
  requestDate: string;
  requiredDate: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected' | 'completed';
  approvedBy?: string;
  approvalDate?: string;
}

interface PurchaseRequestItem {
  id: string;
  description: string;
  quantity: number;
  estimatedPrice: number;
  specification: string;
  urgency: 'low' | 'medium' | 'high';
}

interface PurchaseOrder {
  id: string;
  orderNumber: string;
  vendorId: string;
  vendorName: string;
  items: PurchaseOrderItem[];
  subtotal: number;
  tax: number;
  total: number;
  orderDate: string;
  expectedDeliveryDate: string;
  status: 'draft' | 'sent' | 'confirmed' | 'delivered' | 'cancelled';
}

interface PurchaseOrderItem {
  id: string;
  productId: string;
  productName: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

// Vendor interface matching backend response (snake_case)
export interface Vendor {
  id: string;
  vendor_code: string;
  company_name: string;
  contact_person: string;
  email?: string;
  phone?: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  gstin?: string;
  pan?: string;
  vendor_type: 'INDIVIDUAL' | 'COMPANY' | 'HUF' | 'PARTNERSHIP';
  tds_category: string;
  credit_days: number;
  bank_name?: string;
  bank_account?: string;
  ifsc_code?: string;
  is_active: boolean;
  created_at: string;
  total_business?: number;
  pending_amount?: number;
  gstDetails?: any;
  panValid?: boolean;
  totalBusinessFormatted?: string;
  pendingAmountFormatted?: string;
}

export interface TDSCategory {
  code: string;
  rate: number;
  threshold: number;
  section: string;
  description: string;
}

interface VendorsResponse {
  vendors: Vendor[];
  count: number;
}

interface TDSCategoriesResponse {
  categories: TDSCategory[];
}

interface VendorResponse {
  vendor: Vendor;
}

// TDS Deduction interfaces
export interface TDSDeduction {
  id: string;
  vendor_id: string;
  vendor_name?: string;
  vendor_pan?: string;
  vendor_gstin?: string;
  invoice_id?: string;
  invoice_no: string;
  invoice_date: string;
  invoice_amount: number;
  tds_section: string;
  tds_rate: number;
  tds_amount: number;
  payment_date?: string;
  certificate_number?: string;
  certificate_date?: string;
  status: 'DEDUCTED' | 'PENDING_CERTIFICATE' | 'CERTIFICATE_GENERATED';
  financial_year: string;
  remarks?: string;
  created_at: string;
}

interface TDSDeductionSummary {
  totalDeductions: number;
  totalInvoiceAmount: number;
  certificatesGenerated: number;
  pendingCertificates: number;
  totalTransactions: number;
}

interface TDSDeductionsResponse {
  deductions: TDSDeduction[];
  summary: TDSDeductionSummary;
  count: number;
}

interface TDSDeductionResponse {
  deduction: TDSDeduction;
}

interface TDSSectionSummary {
  tds_section: string;
  transaction_count: number;
  total_invoice_amount: number;
  total_tds_deducted: number;
  certificates_generated: number;
  pending_certificates: number;
}

interface TDSSummaryResponse {
  summary: TDSSectionSummary[];
  financialYear: string;
}

// Purchase Invoice interfaces
export interface PurchaseInvoice {
  id: string;
  vendor_id: string;
  vendor_name?: string;
  vendor_code?: string;
  vendor_gstin?: string;
  vendor_pan?: string;
  purchase_order_id?: string;
  po_number?: string;
  invoice_no: string;
  invoice_date: string;
  due_date?: string;
  gross_amount: number;
  gst_amount: number;
  tds_amount: number;
  net_amount: number;
  status: 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | 'UNDER_REVIEW';
  payment_status: 'PENDING' | 'PROCESSING' | 'PAID' | 'ON_HOLD';
  approved_by?: string;
  approved_at?: string;
  remarks?: string;
  created_at: string;
}

interface PurchaseInvoiceSummary {
  totalInvoices: number;
  pendingApproval: number;
  approved: number;
  totalAmount: number;
  pendingPayment: number;
}

interface PurchaseInvoicesResponse {
  invoices: PurchaseInvoice[];
  summary: PurchaseInvoiceSummary;
  count: number;
}

interface PurchaseInvoiceResponse {
  invoice: PurchaseInvoice;
}

// Vendor Payment interfaces
export interface VendorPayment {
  id: string;
  payment_number: string;
  vendor_id: string;
  vendor_name?: string;
  vendor_code?: string;
  vendor_pan?: string;
  vendor_gstin?: string;
  vendor_bank_name?: string;
  vendor_bank_account?: string;
  vendor_ifsc_code?: string;
  invoice_id?: string;
  invoice_no?: string;
  payment_date: string;
  gross_amount: number;
  tds_section?: string;
  tds_rate?: number;
  tds_amount: number;
  net_amount: number;
  payment_mode: 'BANK_TRANSFER' | 'CHEQUE' | 'CASH' | 'UPI' | 'NEFT' | 'RTGS';
  bank_reference?: string;
  status: 'PENDING' | 'PROCESSED' | 'COMPLETED' | 'CANCELLED';
  notes?: string;
  tds_deduction_id?: string;
  created_at: string;
}

interface VendorPaymentSummary {
  totalPayments: number;
  pending: number;
  processed: number;
  completed: number;
  totalAmount: number;
  totalTDS: number;
}

interface VendorPaymentsResponse {
  payments: VendorPayment[];
  summary: VendorPaymentSummary;
  count: number;
}

interface VendorPaymentResponse {
  payment: VendorPayment;
}

// Report interfaces
export interface TDSSectionSummaryItem {
  tds_section: string;
  transaction_count: number;
  total_invoice_amount: number;
  total_tds_deducted: number;
  certificates_generated: number;
  pending_certificates: number;
}

export interface TDSMonthlyTrend {
  month: string;
  transaction_count: number;
  total_invoice_amount: number;
  total_tds_deducted: number;
}

export interface TDSTopVendor {
  vendor_id: string;
  vendor_name: string;
  vendor_pan: string;
  transaction_count: number;
  total_invoice_amount: number;
  total_tds_deducted: number;
}

export interface TDSComplianceReport {
  report: {
    financialYear: string;
    totals: {
      total_transactions: number;
      total_invoice_amount: number;
      total_tds_deducted: number;
      certificates_generated: number;
      pending_certificates: number;
    };
    sectionSummary: TDSSectionSummaryItem[];
    monthlyTrend: TDSMonthlyTrend[];
    topVendors: TDSTopVendor[];
  };
}

export interface VendorMetric {
  vendor_id: string;
  company_name: string;
  vendor_code: string;
  state: string;
  vendor_type: string;
  is_active: boolean;
  total_orders: number;
  total_business: number;
  avg_order_value: number;
  completed_business: number;
  pending_business: number;
  completed_orders: number;
  cancelled_orders: number;
}

export interface VendorPerformanceReport {
  report: {
    summary: {
      totalVendors: number;
      activeVendors: number;
      totalBusiness: number;
      avgBusinessPerVendor: number;
    };
    vendorMetrics: VendorMetric[];
    topVendors: VendorMetric[];
    vendorByType: { vendor_type: string; vendor_count: number; active_count: number }[];
    vendorByState: { state: string; vendor_count: number; active_count: number }[];
  };
}

export interface POStatusSummary {
  status: string;
  order_count: number;
  subtotal: number;
  gst_amount: number;
  total_amount: number;
}

export interface MonthlyPurchaseTrend {
  month: string;
  order_count: number;
  subtotal: number;
  gst_amount: number;
  total_amount: number;
}

export interface TopProduct {
  product_id: string;
  product_name: string;
  hsn_code: string;
  order_count: number;
  total_quantity: number;
  total_value: number;
}

export interface PurchaseAnalysisReport {
  report: {
    summary: {
      totalOrders: number;
      totalPOValue: number;
      totalInvoices: number;
      totalInvoiceValue: number;
      pendingPayments: number;
    };
    poByStatus: POStatusSummary[];
    monthlyTrend: MonthlyPurchaseTrend[];
    topProducts: TopProduct[];
    invoiceSummary: {
      total_invoices: number;
      total_gross: number;
      total_gst: number;
      total_tds: number;
      total_net: number;
      approved_count: number;
      paid_count: number;
      pending_amount: number;
    };
    paymentSummary: {
      payment_mode: string;
      payment_count: number;
      total_gross: number;
      total_tds: number;
      total_net: number;
    }[];
  };
}

export interface GSTMonthlyData {
  month: string;
  invoice_count?: number;
  order_count?: number;
  taxable_amount: number;
  gst_amount: number;
}

export interface GSTReconciliationReport {
  report: {
    financialYear: string;
    invoiceGST: GSTMonthlyData[];
    poGST: GSTMonthlyData[];
    totalInvoiceGST: number;
    totalPOGST: number;
  };
}

// ---------------------------------------------------------------------------
// RFQ / Sourcing (backend: erp-api src/routes/rfq.js base '/rfqs' and
// src/routes/supplierQuotation.js base '/supplier-quotations'). Raw
// snake_case shapes straight off src/models/Rfq.js / SupplierQuotation.js --
// DECIMAL columns arrive as strings, so pages coerce with a local toNum().
// ---------------------------------------------------------------------------

export type RfqType = 'MATERIAL' | 'SERVICE' | 'CAPEX' | 'SUBCONTRACTING' | 'JOB_WORK' | 'CONSUMABLE' | 'ASSET' | 'OTHER';

export type RfqStatus =
  | 'DRAFT'
  | 'ISSUED'
  | 'RESPONSES_IN'
  | 'UNDER_EVALUATION'
  | 'AWARDED'
  | 'CANCELLED'
  | 'CLOSED';

export type RfqSupplierStatus = 'INVITED' | 'RESPONDED' | 'DECLINED';

export type QuotationStatus = 'SUBMITTED' | 'SHORTLISTED' | 'REJECTED' | 'AWARDED';

export type TechnicalCompliance = 'COMPLIANT' | 'PARTIAL' | 'NON_COMPLIANT';

export interface RfqRow {
  id: number;
  rfq_number: string;
  rfq_type: RfqType;
  rfq_date: string;
  response_deadline: string | null;
  status: RfqStatus;
  supplier_count?: number;
  quotation_count?: number;
  awarded_vendor_name?: string | null;
  buyer_name?: string | null;
  converted_po_id?: number | null;
  converted_po_number?: string | null;
  [key: string]: any;
}

export interface RfqSummary {
  byStatus: Record<string, { count: number }>;
  totalCount: number;
  openCount: number;
  awaitingResponseCount: number;
}

export interface RfqItem {
  id: number;
  product_id: string | null;
  product_name?: string | null;
  hsn_code?: string | null;
  quantity: number | string;
  uom?: string | null;
  required_date?: string | null;
  specification?: string | null;
}

export interface RfqSupplier {
  id: number;
  vendor_id: string;
  vendor_name: string;
  status: RfqSupplierStatus;
  invited_at?: string | null;
  responded_at?: string | null;
}

export interface RfqQuotationSummary {
  id: number;
  quotation_number: string;
  vendor_id: string;
  vendor_name: string;
  total_amount: number | string;
  landed_cost: number | string | null;
  delivery_days: number | string | null;
  quality_score: number | string | null;
  status: QuotationStatus;
}

export interface RfqDetail extends RfqRow {
  items: RfqItem[];
  suppliers: RfqSupplier[];
  quotations: RfqQuotationSummary[];
}

export interface RfqComparisonColumn {
  quotationId: number;
  vendorId: string;
  vendorName: string;
}

export interface RfqComparisonMetric {
  key: string;
  label: string;
  values: Record<number, number | string | null>;
}

export interface RfqComparison {
  rfq: { id: number; rfq_number: string; rfq_type: RfqType; status: RfqStatus; rfq_date: string; response_deadline: string | null; currency: string };
  columns: RfqComparisonColumn[];
  metrics: RfqComparisonMetric[];
  scores: { quotationId: number; overallScore: number }[];
  best: { commercial: number | null; delivery: number | null; quality: number | null; overall: number | null };
}

export interface SupplierQuotationItem {
  id?: number;
  rfq_item_id?: number | null;
  product_id: string | null;
  item_description?: string | null;
  hsn_code?: string | null;
  quantity: number | string;
  uom?: string | null;
  unit_rate: number | string;
  discount_percent?: number | string;
  tax_rate?: number | string;
  freight?: number | string;
  line_total?: number | string;
  brand_make?: string | null;
  delivery_days?: number | null;
}

export interface SupplierQuotationDetail {
  id: number;
  quotation_number: string;
  rfq_id: number | null;
  vendor_id: string;
  vendor_name?: string;
  quotation_date: string;
  valid_until?: string | null;
  delivery_days?: number | string | null;
  quality_score?: number | string | null;
  technical_compliance?: TechnicalCompliance | null;
  payment_terms?: string | null;
  incoterms?: string | null;
  warranty?: string | null;
  subtotal: number | string;
  discount_amount: number | string;
  freight_amount: number | string;
  insurance_amount: number | string;
  other_charges: number | string;
  tax_amount: number | string;
  total_amount: number | string;
  landed_cost: number | string | null;
  status: QuotationStatus;
  notes?: string | null;
  items: SupplierQuotationItem[];
}

// ---------------------------------------------------------------------------
// Payment Proposal (backend: erp-api src/routes/paymentProposal.js, base
// '/payment-proposal') — the AP pay-run workbench. Money fields arrive as
// plain numbers (already coerced server-side), unlike the RFQ DECIMAL strings
// above.
// ---------------------------------------------------------------------------

export interface PaymentProposalInvoice {
  invoiceId: number;
  vendorId: number | string;
  vendorName: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  daysToDue: number;
  matchStatus: string;
  gross: number;
  tdsSection: string | null;
  tdsRate: number | null;
  tds: number;
  net: number;
  tdsEstimated?: boolean;
}

export interface PaymentProposalBlockedInvoice {
  invoiceId: number;
  vendorName: string;
  invoiceNumber: string;
  dueDate: string;
  gross: number;
  matchStatus: string;
  reason: string;
}

export interface PaymentProposalBankAccount {
  id: number | string;
  name: string;
  bankName: string;
  accountNumber: string;
  isPrimary: boolean;
  balance: number;
}

export interface PaymentProposalVendorGroup {
  vendorId: number | string;
  vendorName: string;
  count: number;
  gross: number;
  tds: number;
  net: number;
  invoices: PaymentProposalInvoice[];
}

export interface PaymentProposal {
  asOf: string;
  horizonDays: number;
  availableCash: number;
  totals: { count: number; gross: number; tds: number; net: number };
  byVendor: PaymentProposalVendorGroup[];
  invoices: PaymentProposalInvoice[];
  blocked: PaymentProposalBlockedInvoice[];
  bankAccounts: PaymentProposalBankAccount[];
  defaultBankAccount: number | string | null;
}

export interface PaymentRunRequest {
  invoiceIds: (number | string)[];
  paymentDate: string;
  bankAccountId: number | string;
  paymentMode: 'NEFT' | 'RTGS' | 'CHEQUE';
}

export interface PaymentRunCreated {
  invoiceId: number;
  paymentId: number | string;
  paymentNumber: string;
  vendorName: string;
  invoiceNumber: string;
  gross: number;
  tds: number;
  net: number;
  status: string;
}

export interface PaymentRunSkipped {
  invoiceId: number;
  status: string;
  reason: string;
}

export interface PaymentRunResult {
  paymentDate: string;
  bankAccountId: number | string;
  paymentMode: string;
  created: PaymentRunCreated[];
  totals: { count: number; gross: number; tds: number; net: number };
  skipped: PaymentRunSkipped[];
}

// ---------------------------------------------------------------------------
// Procurement Analytics (backend: erp-api src/routes/procurementAnalytics.js,
// base '/procurement-analytics') — the intelligence dashboard, distinct from
// the older /procurement/reports/* endpoints above.
// ---------------------------------------------------------------------------

export interface ProcurementAnalytics {
  asOf: string;
  fyStart: string;
  spendByMonth: { month: string; spend: number }[];
  spendByCategory: { category: string; spend: number; share: number }[];
  spendBySupplier: { vendorId: number | string; name: string; spend: number; share: number }[];
  supplierConcentration: { top1Pct: number; top3Pct: number; top5Pct: number; herfindahl: number };
  poAging: { bucket: string; count: number; value: number }[];
  cycleTimes: { prToPoDays: number; poToGrnDays: number; grnToInvoiceDays: number };
  matchRate: {
    matched: number;
    withinTolerance: number;
    exception: number;
    unmatched: number;
    total: number;
    cleanPct: number;
  };
  invoiceExceptionRate: number;
  onTimeDelivery: { onTime: number; late: number; pct: number };
  topExceptions: { type: string; count: number }[];
}

// ---------------------------------------------------------------------------
// Service Entry Sheets (backend: erp-api src/routes base '/service-entries')
// -- the service analogue of a GRN, raised against a SERVICE-type PO.
// ---------------------------------------------------------------------------

export type ServiceEntryStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export const SERVICE_ENTRY_STATUSES: ServiceEntryStatus[] = ['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED', 'CANCELLED'];

export interface ServiceEntryRow {
  id: number;
  ses_number: string;
  po_id: number;
  po_number: string;
  vendor_id: string;
  vendor_name: string;
  service_period_from: string;
  service_period_to: string;
  status: ServiceEntryStatus;
  total_amount: number | string;
}

export interface ServiceEntryLine {
  id: number;
  description: string;
  quantity: number | string;
  uom: string;
  rate: number | string;
  amount: number | string;
  cost_center_id?: number | string | null;
  project_id?: number | string | null;
}

export interface ServiceEntryDetail extends ServiceEntryRow {
  description?: string | null;
  remarks?: string | null;
  lines: ServiceEntryLine[];
}

export interface ServiceEntryLineInput {
  description: string;
  quantity: number;
  uom?: string;
  rate: number;
  costCenterId?: number | null;
  projectId?: number | null;
}

export interface CreateServiceEntryInput {
  poId: number;
  vendorId: string | number;
  servicePeriodFrom: string;
  servicePeriodTo: string;
  description?: string | null;
  remarks?: string | null;
  lines: ServiceEntryLineInput[];
}

// ---------------------------------------------------------------------------
// PO Amendment / CAPEX Capitalization (backend: erp-api src/routes/procurement.js,
// base '/procurement/purchase-orders')
// ---------------------------------------------------------------------------

export interface PoAmendment {
  id: number;
  version: number;
  reason: string;
  changed_by_name: string;
  changed_at: string;
  snapshot_json: any;
}

export interface AmendPoInput {
  reason: string;
  poType?: string;
  priority?: string;
  expectedDeliveryDate?: string | null;
  paymentTerms?: string | null;
  notes?: string | null;
  items?: {
    productId?: string;
    itemType?: string;
    quantity: number;
    unitPrice: number;
    gstRate?: number;
    lineDescription?: string;
    hsnCode?: string;
    uom?: string;
  }[];
}

export interface CapitalizePoInput {
  assetName: string;
  assetCategoryId?: number | string | null;
  capitalizationDate: string;
}

export const procurementService = {
  // Purchase Requisitions (backend: erp-api src/routes/purchaseRequisitionRoutes.js, base '/purchase-requisitions')
  getPurchaseRequests: async (params?: any): Promise<PurchaseRequest[]> => {
    return api.get<PurchaseRequest[]>('/purchase-requisitions', { params });
  },

  getPurchaseRequestsSummary: async (params?: any): Promise<any> => {
    return api.get('/purchase-requisitions/summary', { params });
  },

  getPurchaseRequest: async (id: string): Promise<PurchaseRequest> => {
    return api.get<PurchaseRequest>(`/purchase-requisitions/${id}`);
  },

  createPurchaseRequest: async (request: Omit<PurchaseRequest, 'id'>): Promise<PurchaseRequest> => {
    return api.post<PurchaseRequest>('/purchase-requisitions', request);
  },

  updatePurchaseRequest: async (id: string, request: Partial<PurchaseRequest>): Promise<PurchaseRequest> => {
    return api.put<PurchaseRequest>(`/purchase-requisitions/${id}`, request);
  },

  submitPurchaseRequest: async (id: string): Promise<PurchaseRequest> => {
    return api.post<PurchaseRequest>(`/purchase-requisitions/${id}/submit`);
  },

  approvePurchaseRequest: async (id: string, comment?: string): Promise<PurchaseRequest> => {
    return api.post<PurchaseRequest>(`/purchase-requisitions/${id}/approve`, { comment });
  },

  rejectPurchaseRequest: async (id: string, reason: string): Promise<PurchaseRequest> => {
    return api.post<PurchaseRequest>(`/purchase-requisitions/${id}/reject`, { reason });
  },

  cancelPurchaseRequest: async (id: string): Promise<PurchaseRequest> => {
    return api.post<PurchaseRequest>(`/purchase-requisitions/${id}/cancel`);
  },

  convertPurchaseRequestToPO: async (id: string, payload?: any): Promise<any> => {
    return api.post(`/purchase-requisitions/${id}/convert-to-po`, payload);
  },

  // Purchase Orders
  getPurchaseOrders: async (params?: any): Promise<PurchaseOrder[]> => {
    return api.get<PurchaseOrder[]>('/procurement/purchase-orders', { params });
  },

  getPurchaseOrder: async (id: string): Promise<PurchaseOrder> => {
    return api.get<PurchaseOrder>(`/procurement/purchase-orders/${id}`);
  },

  createPurchaseOrder: async (order: Omit<PurchaseOrder, 'id'>): Promise<PurchaseOrder> => {
    return api.post<PurchaseOrder>('/procurement/purchase-orders', order);
  },

  updatePurchaseOrder: async (id: string, order: Partial<PurchaseOrder>): Promise<PurchaseOrder> => {
    return api.put<PurchaseOrder>(`/procurement/purchase-orders/${id}`, order);
  },

  deletePurchaseOrder: async (id: string): Promise<void> => {
    return api.delete(`/procurement/purchase-orders/${id}`);
  },

  // Purchase Order lifecycle actions
  submitPurchaseOrder: async (id: string): Promise<PurchaseOrder> => {
    return api.post<PurchaseOrder>(`/procurement/purchase-orders/${id}/submit`);
  },

  approvePurchaseOrder: async (id: string, comment?: string): Promise<PurchaseOrder> => {
    return api.post<PurchaseOrder>(`/procurement/purchase-orders/${id}/approve`, { comment });
  },

  rejectPurchaseOrder: async (id: string, reason: string): Promise<PurchaseOrder> => {
    return api.post<PurchaseOrder>(`/procurement/purchase-orders/${id}/reject`, { reason });
  },

  sendPurchaseOrder: async (id: string): Promise<PurchaseOrder> => {
    return api.post<PurchaseOrder>(`/procurement/purchase-orders/${id}/send`);
  },

  acknowledgePurchaseOrder: async (id: string): Promise<PurchaseOrder> => {
    return api.post<PurchaseOrder>(`/procurement/purchase-orders/${id}/acknowledge`);
  },

  cancelPurchaseOrder: async (id: string, reason?: string): Promise<PurchaseOrder> => {
    return api.post<PurchaseOrder>(`/procurement/purchase-orders/${id}/cancel`, { reason });
  },

  // Vendors
  getVendors: async (params?: any): Promise<VendorsResponse> => {
    return api.get<VendorsResponse>('/procurement/vendors', { params });
  },

  getVendor: async (id: string): Promise<VendorResponse> => {
    return api.get<VendorResponse>(`/procurement/vendors/${id}`);
  },

  createVendor: async (vendor: Partial<Vendor>): Promise<VendorResponse> => {
    // Convert snake_case form data to camelCase for backend
    const payload = {
      companyName: vendor.company_name,
      contactPerson: vendor.contact_person,
      email: vendor.email,
      phone: vendor.phone,
      address: vendor.address,
      city: vendor.city,
      state: vendor.state,
      postalCode: vendor.postal_code,
      country: vendor.country,
      gstin: vendor.gstin,
      pan: vendor.pan,
      vendorType: vendor.vendor_type,
      tdsCategory: vendor.tds_category,
      creditDays: vendor.credit_days,
      bankDetails: {
        bankName: vendor.bank_name,
        bankAccount: vendor.bank_account,
        ifscCode: vendor.ifsc_code,
      },
      isActive: vendor.is_active,
    };
    return api.post<VendorResponse>('/procurement/vendors', payload);
  },

  updateVendor: async (id: string, vendor: Partial<Vendor>): Promise<VendorResponse> => {
    // Convert snake_case form data to camelCase for backend
    const payload = {
      companyName: vendor.company_name,
      contactPerson: vendor.contact_person,
      email: vendor.email,
      phone: vendor.phone,
      address: vendor.address,
      city: vendor.city,
      state: vendor.state,
      postalCode: vendor.postal_code,
      gstin: vendor.gstin,
      pan: vendor.pan,
      vendorType: vendor.vendor_type,
      tdsCategory: vendor.tds_category,
      creditDays: vendor.credit_days,
      bankDetails: {
        bankName: vendor.bank_name,
        bankAccount: vendor.bank_account,
        ifscCode: vendor.ifsc_code,
      },
      isActive: vendor.is_active,
    };
    return api.put<VendorResponse>(`/procurement/vendors/${id}`, payload);
  },

  deleteVendor: async (id: string): Promise<void> => {
    return api.delete(`/procurement/vendors/${id}`);
  },

  // TDS Categories
  getTDSCategories: async (): Promise<TDSCategoriesResponse> => {
    return api.get<TDSCategoriesResponse>('/procurement/tds-categories');
  },

  // TDS Deductions
  getTDSDeductions: async (params?: any): Promise<TDSDeductionsResponse> => {
    return api.get<TDSDeductionsResponse>('/procurement/tds-deductions', { params });
  },

  getTDSDeduction: async (id: string): Promise<TDSDeductionResponse> => {
    return api.get<TDSDeductionResponse>(`/procurement/tds-deductions/${id}`);
  },

  createTDSDeduction: async (deduction: Partial<TDSDeduction>): Promise<TDSDeductionResponse> => {
    return api.post<TDSDeductionResponse>('/procurement/tds-deductions', deduction);
  },

  updateTDSDeduction: async (id: string, deduction: Partial<TDSDeduction>): Promise<TDSDeductionResponse> => {
    return api.put<TDSDeductionResponse>(`/procurement/tds-deductions/${id}`, deduction);
  },

  generateTDSCertificate: async (id: string): Promise<TDSDeductionResponse> => {
    return api.post<TDSDeductionResponse>(`/procurement/tds-deductions/${id}/generate-certificate`);
  },

  getTDSSummary: async (financialYear?: string): Promise<TDSSummaryResponse> => {
    return api.get<TDSSummaryResponse>('/procurement/tds-summary', { params: { financialYear } });
  },

  // Purchase Invoices
  getPurchaseInvoices: async (params?: any): Promise<PurchaseInvoicesResponse> => {
    return api.get<PurchaseInvoicesResponse>('/procurement/purchase-invoices', { params });
  },

  getPurchaseInvoice: async (id: string): Promise<PurchaseInvoiceResponse> => {
    return api.get<PurchaseInvoiceResponse>(`/procurement/purchase-invoices/${id}`);
  },

  // Accepts either a header-only bill (subtotal + GST amounts) or a line-item,
  // PO-linked bill ({ items:[{ poItemId, productId, quantity, unitPrice, gstRate, hsnCode, description }], allowDuplicate }).
  createPurchaseInvoice: async (invoice: any): Promise<PurchaseInvoiceResponse> => {
    return api.post<PurchaseInvoiceResponse>('/procurement/purchase-invoices', invoice);
  },

  // POs of a given type for the invoice-from-PO workspace. Defaults to the
  // billable stage rather than a single status: a PO is normally billed after
  // goods receipt, when it is PARTIALLY_RECEIVED/FULLY_RECEIVED and no longer
  // APPROVED. The API owns that list (PO_INVOICEABLE_STATUSES); pass an explicit
  // `status` only to narrow to one state.
  getPurchaseOrdersByType: async (poType: string, status?: string): Promise<any[]> => {
    const params: Record<string, any> = { poType };
    if (status) params.status = status;
    else params.invoiceable = 'true';
    const res: any = await api.get('/procurement/purchase-orders', { params });
    return (res?.purchaseOrders || res || []) as any[];
  },

  // PO lines with ordered/received/invoiced/available-to-invoice.
  getPoInvoiceLines: async (poId: string | number): Promise<any[]> => {
    const res: any = await api.get(`/procurement/purchase-orders/${poId}/invoice-lines`);
    return (res?.items || []) as any[];
  },

  updatePurchaseInvoice: async (id: string, invoice: Partial<PurchaseInvoice>): Promise<PurchaseInvoiceResponse> => {
    return api.put<PurchaseInvoiceResponse>(`/procurement/purchase-invoices/${id}`, invoice);
  },

  approvePurchaseInvoice: async (id: string): Promise<PurchaseInvoiceResponse> => {
    return api.post<PurchaseInvoiceResponse>(`/procurement/purchase-invoices/${id}/approve`);
  },

  // Vendor Payments
  getVendorPayments: async (params?: any): Promise<VendorPaymentsResponse> => {
    return api.get<VendorPaymentsResponse>('/procurement/vendor-payments', { params });
  },

  getVendorPayment: async (id: string): Promise<VendorPaymentResponse> => {
    return api.get<VendorPaymentResponse>(`/procurement/vendor-payments/${id}`);
  },

  createVendorPayment: async (payment: Record<string, any>): Promise<VendorPaymentResponse> => {
    return api.post<VendorPaymentResponse>('/procurement/vendor-payments', payment);
  },

  updateVendorPayment: async (id: string, payment: Partial<VendorPayment>): Promise<VendorPaymentResponse> => {
    return api.put<VendorPaymentResponse>(`/procurement/vendor-payments/${id}`, payment);
  },

  processVendorPayment: async (id: string): Promise<VendorPaymentResponse> => {
    return api.post<VendorPaymentResponse>(`/procurement/vendor-payments/${id}/process`);
  },

  completeVendorPayment: async (id: string, bankReference?: string): Promise<VendorPaymentResponse> => {
    return api.post<VendorPaymentResponse>(`/procurement/vendor-payments/${id}/complete`, { bankReference });
  },

  // Reports
  getProcurementReport: async (startDate: string, endDate: string): Promise<any> => {
    return api.get('/procurement/reports', {
      params: { startDate, endDate },
    });
  },

  getTDSComplianceReport: async (financialYear?: string): Promise<TDSComplianceReport> => {
    return api.get<TDSComplianceReport>('/procurement/reports/tds-compliance', {
      params: { financialYear },
    });
  },

  getVendorPerformanceReport: async (fromDate?: string, toDate?: string): Promise<VendorPerformanceReport> => {
    return api.get<VendorPerformanceReport>('/procurement/reports/vendor-performance', {
      params: { fromDate, toDate },
    });
  },

  getPurchaseAnalysisReport: async (fromDate?: string, toDate?: string): Promise<PurchaseAnalysisReport> => {
    return api.get<PurchaseAnalysisReport>('/procurement/reports/purchase-analysis', {
      params: { fromDate, toDate },
    });
  },

  getGSTReconciliationReport: async (financialYear?: string): Promise<GSTReconciliationReport> => {
    return api.get<GSTReconciliationReport>('/procurement/reports/gst-reconciliation', {
      params: { financialYear },
    });
  },

  // Procurement Analytics (Command Center / Exceptions / Vendor 360)
  getProcurementCommandCenter: async (): Promise<any> => {
    return api.get('/procurement-analytics/command-center');
  },

  getProcurementExceptions: async (): Promise<any> => {
    return api.get('/procurement-analytics/exceptions');
  },

  getSupplier360: async (id: string): Promise<any> => {
    return api.get(`/procurement-analytics/supplier-360/${id}`);
  },

  getProcurementAnalytics: async (): Promise<ProcurementAnalytics> => {
    return api.get<ProcurementAnalytics>('/procurement-analytics/analytics');
  },

  // Payment Proposal (AP pay-run workbench)
  getPaymentProposal: async (horizonDays: number = 7): Promise<PaymentProposal> => {
    return api.get<PaymentProposal>('/payment-proposal', { params: { horizonDays } });
  },

  createPaymentRun: async (payload: PaymentRunRequest): Promise<PaymentRunResult> => {
    return api.post<PaymentRunResult>('/payment-proposal/run', payload);
  },

  // RFQ / Sourcing (backend: erp-api src/routes/rfq.js, base '/rfqs')
  getRfqs: async (params?: any): Promise<{ rfqs: RfqRow[]; count: number }> => {
    return api.get('/rfqs', { params });
  },

  getRfqSummary: async (): Promise<{ summary: RfqSummary }> => {
    return api.get('/rfqs/summary');
  },

  getRfq: async (id: string | number): Promise<{ rfq: RfqDetail }> => {
    return api.get(`/rfqs/${id}`);
  },

  getRfqComparison: async (id: string | number): Promise<{ comparison: RfqComparison }> => {
    return api.get(`/rfqs/${id}/comparison`);
  },

  createRfq: async (data: Record<string, any>): Promise<{ rfq: RfqDetail }> => {
    return api.post('/rfqs', data);
  },

  updateRfq: async (id: string | number, data: Record<string, any>): Promise<{ rfq: RfqDetail }> => {
    return api.put(`/rfqs/${id}`, data);
  },

  inviteRfqSuppliers: async (id: string | number, vendorIds: (string | number)[]): Promise<{ rfq: RfqDetail }> => {
    return api.post(`/rfqs/${id}/invite`, { vendorIds });
  },

  issueRfq: async (id: string | number): Promise<{ rfq: RfqDetail }> => {
    return api.post(`/rfqs/${id}/issue`);
  },

  cancelRfq: async (id: string | number): Promise<{ rfq: RfqDetail }> => {
    return api.post(`/rfqs/${id}/cancel`);
  },

  awardRfq: async (
    id: string | number,
    payload: { quotationId: number; justification?: string; createPo?: boolean; poDate?: string; deliveryDate?: string }
  ): Promise<{ rfq: RfqDetail; purchaseOrderId: number | null }> => {
    return api.post(`/rfqs/${id}/award`, payload);
  },

  // Supplier Quotations (backend: erp-api src/routes/supplierQuotation.js, base '/supplier-quotations')
  getSupplierQuotations: async (params?: any): Promise<{ quotations: RfqQuotationSummary[]; count: number }> => {
    return api.get('/supplier-quotations', { params });
  },

  getSupplierQuotation: async (id: string | number): Promise<{ quotation: SupplierQuotationDetail }> => {
    return api.get(`/supplier-quotations/${id}`);
  },

  createSupplierQuotation: async (data: Record<string, any>): Promise<{ quotation: SupplierQuotationDetail }> => {
    return api.post('/supplier-quotations', data);
  },

  shortlistQuotation: async (id: string | number): Promise<{ quotation: SupplierQuotationDetail }> => {
    return api.post(`/supplier-quotations/${id}/shortlist`);
  },

  rejectQuotation: async (id: string | number): Promise<{ quotation: SupplierQuotationDetail }> => {
    return api.post(`/supplier-quotations/${id}/reject`);
  },

  // Service Entry Sheets (backend: base '/service-entries')
  getServiceEntries: async (params?: any): Promise<{ serviceEntries: ServiceEntryRow[]; count: number }> => {
    return api.get('/service-entries', { params });
  },

  getServiceEntriesSummary: async (): Promise<any> => {
    return api.get('/service-entries/summary');
  },

  getServiceEntry: async (id: string | number): Promise<{ serviceEntry: ServiceEntryDetail }> => {
    return api.get(`/service-entries/${id}`);
  },

  createServiceEntry: async (payload: CreateServiceEntryInput): Promise<{ serviceEntry: ServiceEntryDetail }> => {
    return api.post('/service-entries', payload);
  },

  updateServiceEntry: async (
    id: string | number,
    payload: Partial<CreateServiceEntryInput>
  ): Promise<{ serviceEntry: ServiceEntryDetail }> => {
    return api.put(`/service-entries/${id}`, payload);
  },

  submitServiceEntry: async (id: string | number): Promise<{ serviceEntry: ServiceEntryDetail }> => {
    return api.post(`/service-entries/${id}/submit`);
  },

  approveServiceEntry: async (id: string | number): Promise<{ serviceEntry: ServiceEntryDetail }> => {
    return api.post(`/service-entries/${id}/approve`);
  },

  rejectServiceEntry: async (id: string | number, reason: string): Promise<{ serviceEntry: ServiceEntryDetail }> => {
    return api.post(`/service-entries/${id}/reject`, { reason });
  },

  // PO Amendment / CAPEX Capitalization
  amendPurchaseOrder: async (id: string | number, payload: AmendPoInput): Promise<any> => {
    return api.post(`/procurement/purchase-orders/${id}/amend`, payload);
  },

  getPoAmendments: async (id: string | number): Promise<{ amendments: PoAmendment[] }> => {
    return api.get(`/procurement/purchase-orders/${id}/amendments`);
  },

  capitalizePo: async (id: string | number, payload: CapitalizePoInput): Promise<any> => {
    return api.post(`/procurement/purchase-orders/${id}/capitalize`, payload);
  },

  // Vendor ledger / statement: opening → dated bills/payments → closing.
  getVendorLedger: async (
    vendorId: string | number,
    params?: { fromDate?: string; toDate?: string },
  ): Promise<{
    vendorId: number; opening: number; closing: number;
    totals: { invoiced: number; paid: number };
    rows: Array<{ date: string; type: string; reference: string; refId: number; credit: number; debit: number; tds?: number; balance: number }>;
  }> => {
    return api.get(`/procurement/vendors/${vendorId}/ledger`, { params });
  },
};